import React, { useMemo, useState } from 'react';
import { Box, Skeleton, Typography, Stack, Chip, Button } from '@mui/material';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { NoteAccordion } from '@/components/cards/NoteAccordion';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useListNotesQuery } from '@/features/engagements/engagementsApi';
import { safelyParseDate } from '@/utils/date';

export const NotesTab = () => {
	const [folderFilter, setFolderFilter] = useState('all');
	const [allNotesExpanded, setAllNotesExpanded] = useState(true);
	const [expandAllKey, setExpandAllKey] = useState(0);

	const {
		clientId,
		dealId,
		relationshipCompanyId,
		isClientView: isClient,
		isRelationshipCompanyView,
	} = useObjectContext();

	const noteFilters = isRelationshipCompanyView
		? { relationshipCompanyId }
		: isClient
			? { clientId }
			: { dealId };

	const {
		data: notesData,
		isError,
		isSuccess,
	} = useListNotesQuery(
		{ filters: noteFilters, limit: 200 },
		{
			skip: !Object.values(noteFilters).some(Boolean),
		},
	);

	const notes = notesData?.data ?? [];

	const foldersWithCount = useMemo(() => {
		return notes.reduce((acc, note) => {
			const folderName = note.folderName;
			if (!folderName) return acc;
			if (acc[folderName]) {
				acc[folderName]++;
			} else {
				acc[folderName] = 1;
			}
			return acc;
		}, {});
	}, [notes]);

	const filteredNotes = useMemo(
		() =>
			notes
				.filter((n) => folderFilter === 'all' || n.folderName === folderFilter)
				.sort(
					(a, b) =>
						(safelyParseDate(b.timestamp)?.getTime() ?? 0) -
						(safelyParseDate(a.timestamp)?.getTime() ?? 0),
				),
		[notes, folderFilter],
	);

	const handleToggleAll = () => {
		setAllNotesExpanded((prev) => !prev);
		setExpandAllKey((prev) => prev + 1);
	};

	return (
		<Box px={'0.5em'}>
			{!isError && !isSuccess ? (
				<Skeleton width='100%' height={'5em'} />
			) : filteredNotes.length > 0 ? (
				<>
					<Stack
						direction='row'
						spacing={1}
						useFlexGap
						flexWrap='wrap'
						alignItems='center'
						sx={{ mb: '1em' }}
					>
						<Chip
							size='small'
							label={`All (${notes.length})`}
							color={folderFilter === 'all' ? 'primary' : 'default'}
							variant={folderFilter === 'all' ? 'filled' : 'outlined'}
							onClick={() => setFolderFilter('all')}
						/>
						{Object.entries(foldersWithCount).map(([folderName, count]) => (
							<Chip
								key={`note-folder-filter-${folderName}`}
								size='small'
								label={`${folderName} (${count})`}
								color={folderFilter === folderName ? 'primary' : 'default'}
								variant={folderFilter === folderName ? 'filled' : 'outlined'}
								onClick={() => setFolderFilter(folderName)}
							/>
						))}
						<Button
							size='small'
							color='primary'
							onClick={handleToggleAll}
							endIcon={allNotesExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
							sx={{ ml: 'auto' }}
						>
							{allNotesExpanded ? 'Collapse all' : 'Expand all'}
						</Button>
					</Stack>
					{filteredNotes.map((note, index) => (
						<NoteAccordion
							key={`note-tab-note-${note.id ?? index}-${expandAllKey}`}
							note={note}
							expandedByDefault={allNotesExpanded}
						/>
					))}
				</>
			) : (
				<Typography textAlign={'center'}>No notes created yet.</Typography>
			)}
		</Box>
	);
};
NotesTab.displayName = 'NotesTab';
